import React from 'react';

import HighlightItem from '@/components/sections/news-activities/highlight-item';
import Item from '@/components/sections/news-activities/item';
import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { HeadingLevel1 } from '@/components/shared/headline/heading-level-1';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Pagination } from '@/components/shared/navigation/pagination';
import { Tabs } from '@/components/shared/navigation/tabs';
import { INewsItem } from '@/types/news/news';
import { IButton } from '@/types/shared';

export interface NewsCategory {
  id: number;
  name: string;
  url: string;
  active: boolean;
}
export interface NewsListProps {
  children?: React.ReactNode;
  title: string;
  description: string;
  buttons?: IButton[];
  items: INewsItem[];
  category: NewsCategory[];
  pagination: {
    current: number;
    total: number;
  };
  onClickPage?: (page) => void;
  dataTestSection?: string;
  dataTest?: string;
  dataTrack?: string;
  notFound?: string;
  highlight?: INewsItem[];
}

const ViewList = (props: NewsListProps) => {
  return (
    <>
      {props.items.map((item, index) => (
        <Box
          key={index}
          test={{
            dataTest: `${props.dataTest}_item${index}`,
          }}
        >
          <Item {...item} />
        </Box>
      ))}
    </>
  );
};

const NewsListSection: React.FC<NewsListProps> = ({
  items = [],
  dataTrack = 'news-list',
  dataTestSection = 'section_news_list',
  dataTest = 'news_list',
  ...props
}) => {
  return (
    <Section test={dataTestSection}>
      <HeadingLevel1
        title={props.title}
        description={props.description}
        buttons={props.buttons}
      />
      {props?.category.length > 0 && (
        <Tabs
          category={props.category}
          dataTrack={dataTrack}
          dataTest={dataTest}
        />
      )}
      <Section as="div" test="news_activities">
        {items.length === 0 &&
          !!props.highlight &&
          props.highlight.length === 0 && (
            <Section
              as="div"
              size="xl"
              backgroundColor="gradient-gray-light"
              test="news_activities_not_found"
            >
              <Container>
                <Box
                  textAlign="center"
                  css={{
                    py: '$space-24',
                    '@sm': {
                      py: '$space-28',
                    },
                  }}
                >
                  <Grid alignItems="center" justifyContent="center" rowGap="2">
                    <Box>
                      <Icon icon="face-sad" size="lg" color="icon-gray-light" />
                    </Box>
                    <Text as="p" variant="text-gray">
                      {props.notFound}
                    </Text>
                  </Grid>
                </Box>
              </Container>
            </Section>
          )}
        {!!props.highlight && props.highlight.length > 0 && (
          <Section
            as="div"
            size="xl"
            backgroundColor="gradient-gray-light"
            test="news_activities_highlight"
          >
            <Container>
              <HighlightItem
                highlight={props.highlight}
                dataTest={dataTest}
                dataTrack={dataTrack}
              />
            </Container>
          </Section>
        )}
        {items.length > 0 && (
          <Section as="div" size="xl" test="news_activities_list">
            <Container>
              <Grid
                css={{
                  rowGap: '$space-8',
                  '@sm': {
                    rowGap: '$space-10',
                  },
                  '@md': {
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    columnGap: '$space-10',
                  },
                  '@lg': {
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    columnGap: '$space-12',
                  },
                }}
              >
                <ViewList {...props} items={items} />
              </Grid>
            </Container>
            {!!props.pagination && props.pagination.total > 1 && (
              <Pagination
                pagination={props.pagination}
                onClickPage={props.onClickPage}
                dataTest={`${dataTest}_pagination`}
              />
            )}
          </Section>
        )}
      </Section>
    </Section>
  );
};
NewsListSection.displayName = 'NewsListSection';
export default NewsListSection;
